import { useLazyQuery } from '@apollo/client';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { DEFAULT_ERROR_TEXT } from '@utils';
import { MAIN_THEME } from '@styles';
import { claimCoverHeaders, steps } from './content';
import {
  getPolicyCoverClaimCoverList,
  getPolicyCoverDetailsInfo,
} from './queries';
import {
  entityToPolicyCoverDetails,
  mapToClaimCoversListingData,
} from './utils';
import { useAppSelector } from '@redux/hooks';
import EnhancedStepperSpecificDesign from '@components/stepper/EnhancedStepperSpecificDesign';
import { makeStyles } from 'tss-react/mui';
import StaticLayout from '@components/layout/StaticLayout';
import CoverDrawer from '../policy-cover-drawer/CoverDrawer';
import { IListingData } from '@models/listing';
import WidgetSection from '@components/widgets/display-widget/WidgetSection';
import { capitalizeFirstLetter } from '@utils/formatting-utils';
import ClaimCoverDrawer from '../claim-cover-drawer/ClaimCoverDrawer';
import { capitalizeFirstCharacter } from '@utils/formatting-utils';
import CoverEntityInfoWidget from './widgets/CoverEntityInfoWidget';
import { IAbstractRecord } from '@models';
import { ActionTarget, IEnhancedMenuItem } from '@components/table/types';
import { toast } from 'react-toastify';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import ListingTableWithPagination from '@components/table/with-pagination/ListingTableWithPagination';
import Loader from '@components/loader/Loader';
import { CDN_URL } from '@utils';

const useStyles = makeStyles()(() => ({
  stepper: {
    backgroundColor: 'transparent',
    maxWidth: '90%',
    marginBottom: '20px',
    '& .MuiStepLabel-root .Mui-active, .MuiStepLabel-root ': {
      color: MAIN_THEME.color.black,
      '& .MuiStepIcon-text': {
        fontSize: 0,
        lineHeight: '0px',
        fill: 'black',
      },
    },

    '.Mui-completed': {
      color: MAIN_THEME.color.black,
      '& .MuiStepIcon-text': {
        fontSize: 0,
        lineHeight: '0px',
        fill: 'black',
      },
    },

    '@media only screen and (min-width: 600px)': {
      maxWidth: '1300px',
    },
  },
  content: {
    padding: '0',
  },
  tabPanelStyle: {
    padding: 'unset',
    marginTop: '20px',
  },
}));

const CoverDetailsPage: React.FC = () => {
  const { classes } = useStyles();
  const params = useParams();
  const user = useAppSelector((state) => state.user);

  const [policyCoverDrawerOpen, setPolicyCoverDrawerOpen] = useState<{
    open: boolean;
    id?: string;
  }>({
    open: false,
  });

  const [policyCoverDetailsInfo, setPolicyCoverDetailsInfo] =
    useState<IAbstractRecord>({});

  const entityId = params.id;

  const [claimCoverTableData, setClaimCoverTableData] = useState<IListingData>({
    pagedItems: {},
    pageSize: 10,
    pageNumber: 1,
    totalCount: 0,
  });

  const [entityResultQuery, { loading }] = useLazyQuery(
    getPolicyCoverDetailsInfo()
  );
  const [coverListResultQuery] = useLazyQuery(getPolicyCoverClaimCoverList());

  const loadCoverList = async (
    currentPage: number,
    pageSize = claimCoverTableData.pageSize,
    searchKey?: string,
    order?: string,
    orderBy?: string
  ) => {
    const result = await coverListResultQuery({
      variables: {
        currentPage,
        currentPageSize: pageSize,
        SelectedLineId: policyCoverDetailsInfo?.lineId,
        selectedPolicyCoverID: params.id,
        Attribute: capitalizeFirstCharacter(
          orderBy || 'planConfigManagement_ClaimCover_Name'
        ),
        Descending: order == 'asc' ? false : true,
        keywordSearch: searchKey || null,
      },
      errorPolicy: 'all',
    });

    return result;
  };

  const loadEntityData = async () => {
    const result = await entityResultQuery({
      variables: { id: entityId },
      errorPolicy: 'all',
    });

    return result;
  };

  const initialize = async () => {
    const entityData = await loadEntityData();

    const coverDetails = await entityToPolicyCoverDetails(entityData.data);

    setPolicyCoverDetailsInfo(coverDetails);
  };

  useEffect(() => {
    initialize();
  }, []);

  const [claimCoverDrawerOpen, setClaimCoverDrawerOpen] = useState<{
    open: boolean;
    id?: string;
  }>({
    open: false,
  });

  const isAdmin = user.userRoles.includes('Insurance-Admin');
  const claimCoverListingActions: IEnhancedMenuItem[] = isAdmin
    ? [
        {
          name: 'new',
          title: 'New',
          onClick: () => {
            setClaimCoverDrawerOpen({
              open: true,
            });
          },
          target: ActionTarget.Entity,
          rowIndependant: true,
          iconUri: CDN_URL + '/icons/add-primary-red.svg',
        },
        {
          name: 'edit',
          title: 'Edit',
          onClick: (selected) => {
            setClaimCoverDrawerOpen({
              open: true,
              id: selected?.[0],
            });
          },
          target: ActionTarget.Entity,
          iconUri: CDN_URL + '/icons/edit-secondary.svg',
        },
      ]
    : [];

  async function handleClaimCoverPageChange(
    page: number,
    searchKey?: string,
    rowsPerPage?: number,
    order?: string,
    orderBy?: string
  ) {
    const result = await loadCoverList(
      page,
      rowsPerPage || claimCoverTableData.pageSize,
      searchKey,
      order,
      orderBy
    );
    if (result.error) {
      toast.error(<ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>);
      return;
    }
    const mappedSubList = mapToClaimCoversListingData(result.data);
    setClaimCoverTableData(mappedSubList);
  }

  const renderMainChildren = () => {
    return (
      <>
        <div style={{ marginTop: '20px' }}>
          <EnhancedStepperSpecificDesign
            activeStep={''}
            steps={steps}
            className={classes.stepper}
            showStepperButton={false}
            style={{ visibility: 'hidden' }}
          />
          {policyCoverDrawerOpen.open && (
            <CoverDrawer
              open={policyCoverDrawerOpen.open}
              onClose={() => setPolicyCoverDrawerOpen({ open: false })}
              onSuccess={() => {
                initialize();
              }}
              isLineNameDisabled={true}
              lineId={policyCoverDetailsInfo?.lineId}
              canNavigateToDetailsPage={false}
              policyCoverId={entityId}
            />
          )}
          <WidgetSection style={{ margin: '-23px 0 0 0' }}>
            <form>
              {
                <div
                  style={{
                    marginTop: '20px',
                    padding: '-23px 0 0 0 !important',
                  }}
                >
                  <ListingTableWithPagination
                    inlineTitle={capitalizeFirstLetter('Claim Covers')}
                    name="claimcovers"
                    orderByAscendingByDefault
                    defaultOrderByColumn="planConfigManagement_ClaimCover_Name"
                    inline
                    data={claimCoverTableData}
                    headers={claimCoverHeaders}
                    onPageChange={handleClaimCoverPageChange}
                    actions={claimCoverListingActions}
                  />
                  {claimCoverDrawerOpen.open && (
                    <ClaimCoverDrawer
                      open={claimCoverDrawerOpen.open}
                      lineId={policyCoverDetailsInfo?.lineId}
                      lineName={policyCoverDetailsInfo?.lineName}
                      claimCoverId={claimCoverDrawerOpen.id}
                      onClose={() => setClaimCoverDrawerOpen({ open: false })}
                      policyCoverId={entityId}
                      isPolicyCoverNameDisabled={true}
                      onSuccess={() => {
                        initialize();
                      }}
                    />
                  )}
                </div>
              }
            </form>
          </WidgetSection>
        </div>
      </>
    );
  };

  const renderLeftSection = () => {
    return (
      <CoverEntityInfoWidget
        data={policyCoverDetailsInfo}
        handleIconClick={() => {
          setPolicyCoverDrawerOpen({ open: true });
        }}
      />
    );
  };

  //#endregion

  return loading ? (
    <Loader />
  ) : (
    <StaticLayout
      name={'Policy Cover Details'}
      leftChildren={renderLeftSection()}
      mainChildren={renderMainChildren()}
    />
  );
};

export default CoverDetailsPage;
